import React from 'react'
import { useIntl } from 'react-intl'
import { Box, FormHelperText } from '@mui/material'
import Checkbox from '@mui/material/Checkbox'
import FormControlLabel from '@mui/material/FormControlLabel'
import SingleSelect from '../../../../../../components/muiComponents/singleSelect/SingleSelect'
import { Lorum } from '../../../../../../utils/constants'
import { H6 } from '../../../../../../layouts/TextStyles'
import CustomInput from '../../../../../../components/muiComponents/customInput'
import { CoursePriceState, CourseState } from '../../../../../../features/types'

interface Props {
    courses: CourseState[]
    values: CoursePriceState
    errors: any
    handleChange: (e: React.ChangeEvent<unknown>) => void
    handleBlur: (e: React.FocusEvent<unknown, Element>) => void
}

const styles = {
    error: {
        color: '#d32f2f',
        marginLeft: 2,
    },
    flex: {
        display: 'flex',
    },
    fullWidth: {
        width: '100%',
    },
}

const PriceTab: React.FC<Props> = ({
    courses,
    values,
    errors,
    handleChange,
    handleBlur,
}) => {
    const { formatMessage } = useIntl()

    return (
        <Box>
            <Box sx={styles.flex}>
                <Box
                    sx={styles.fullWidth}
                    mr={4}
                >
                    <Box mb={2}>
                        <H6>{formatMessage({ id: 'nameKz' })}</H6>

                        <CustomInput
                            id="titleKz"
                            name="titleKz"
                            value={values?.titleKz}
                            onChange={handleChange}
                            onBlur={handleBlur}
                            error={Boolean(errors.titleKz)}
                            helperText={errors.titleKz}
                            placeholder={formatMessage({
                                id: 'nameSubject',
                            })}
                            fullWidth
                        />
                    </Box>

                    <Box mb={3}>
                        <H6>{formatMessage({ id: 'sloganKz' })}</H6>

                        <CustomInput
                            id="sloganKz"
                            name="sloganKz"
                            value={values?.sloganKz}
                            onChange={handleChange}
                            onBlur={handleBlur}
                            error={Boolean(errors.sloganKz)}
                            helperText={errors.sloganKz}
                            placeholder={Lorum}
                            fullWidth
                            multiline
                            maxRows={5}
                            minRows={5}
                        />
                    </Box>

                    <Box>
                        <H6>{formatMessage({ id: 'course' })}</H6>

                        <Box mt={1}>
                            <SingleSelect
                                id="courseId"
                                name="courseId"
                                value={values?.courseId}
                                handleChange={handleChange}
                                handleBlur={handleBlur}
                                handleError={Boolean(errors.courseId)}
                                menuItems={courses}
                                placeholder={formatMessage({
                                    id: 'course',
                                })}
                            />

                            <FormHelperText sx={styles.error}>
                                {errors.courseId}
                            </FormHelperText>
                        </Box>
                    </Box>

                    <Box
                        sx={styles.flex}
                        mt={2}
                    >
                        <Box>
                            <H6>
                                {formatMessage({ id: 'requiredSubjectsCount' })}
                            </H6>

                            <Box>
                                <CustomInput
                                    id="requiredSubjectCount"
                                    name="requiredSubjectCount"
                                    type="number"
                                    value={values?.requiredSubjectCount}
                                    onChange={handleChange}
                                    onBlur={handleBlur}
                                    error={Boolean(errors.requiredSubjectCount)}
                                    helperText={errors.requiredSubjectCount}
                                    fullWidth
                                />
                            </Box>
                        </Box>

                        <Box ml={2}>
                            <H6>
                                {formatMessage({
                                    id: 'notRequiredSubjectCount',
                                })}
                            </H6>

                            <Box>
                                <CustomInput
                                    id="notRequiredSubjectCount"
                                    name="notRequiredSubjectCount"
                                    type="number"
                                    value={values?.notRequiredSubjectCount}
                                    onChange={handleChange}
                                    onBlur={handleBlur}
                                    error={Boolean(
                                        errors.notRequiredSubjectCount,
                                    )}
                                    helperText={errors.notRequiredSubjectCount}
                                    fullWidth
                                />
                            </Box>
                        </Box>
                    </Box>
                </Box>

                <Box sx={styles.fullWidth}>
                    <Box mb={2}>
                        <H6>{formatMessage({ id: 'nameRu' })}</H6>

                        <CustomInput
                            id="titleRu"
                            name="titleRu"
                            value={values?.titleRu}
                            onChange={handleChange}
                            onBlur={handleBlur}
                            error={Boolean(errors.titleRu)}
                            helperText={errors.titleRu}
                            placeholder={formatMessage({
                                id: 'nameSubject',
                            })}
                            fullWidth
                        />
                    </Box>

                    <Box mb={3}>
                        <H6>{formatMessage({ id: 'sloganRu' })}</H6>

                        <CustomInput
                            id="sloganRu"
                            name="sloganRu"
                            value={values?.sloganRu}
                            onChange={handleChange}
                            onBlur={handleBlur}
                            error={Boolean(errors.sloganRu)}
                            helperText={errors.sloganRu}
                            placeholder={Lorum}
                            fullWidth
                            multiline
                            maxRows={5}
                            minRows={5}
                        />
                    </Box>

                    <Box sx={{...styles.flex, gap: '8px'}}>
                        <Box
                        >
                            <H6>
                                {formatMessage({ id: 'lasting' })} (
                                {formatMessage({ id: 'day' })})
                            </H6>

                            <CustomInput
                                id="duration"
                                name="duration"
                                value={values?.duration}
                                onChange={handleChange}
                                onBlur={handleBlur}
                                error={Boolean(errors.duration)}
                                helperText={errors.duration}
                                placeholder={formatMessage({
                                    id: 'min',
                                })}
                                fullWidth
                            />
                        </Box>

                        <Box >
                            <H6>{formatMessage({ id: 'price' })}</H6>

                            <CustomInput
                                id="price"
                                name="price"
                                type="number"
                                value={values?.price}
                                onChange={handleChange}
                                onBlur={handleBlur}
                                error={Boolean(errors.price)}
                                helperText={errors.price}
                                placeholder={formatMessage({
                                    id: 'price',
                                })}
                                fullWidth
                            />
                        </Box>

                        <Box>
                            <H6>{formatMessage({ id: 'optimalPrice' })}</H6>

                            <CustomInput
                                id="optimalPrice"
                                name="optimalPrice"
                                type="number"
                                value={values?.optimalPrice}
                                onChange={handleChange}
                                onBlur={handleBlur}
                                error={Boolean(errors.optimalPrice)}
                                helperText={errors.optimalPrice}
                                placeholder={formatMessage({
                                    id: 'optimalPrice',
                                })}
                                fullWidth
                            />
                        </Box>
                    </Box>

                    <Box>
                        <FormControlLabel
                            control={
                                <Checkbox
                                    name="requiredSubjectsMandatory"
                                    checked={values?.requiredSubjectsMandatory}
                                    onChange={handleChange}
                                />
                            }
                            label={formatMessage({
                                id: 'requiredSubjectsMandatory',
                            })}
                        />
                    </Box>

                    <Box>
                        <FormControlLabel
                            control={
                                <Checkbox
                                    name="isMonthly"
                                    checked={values?.isMonthly}
                                    onChange={handleChange}
                                />
                            }
                            label={formatMessage({
                                id: 'isMonthly',
                            })}
                        />
                    </Box>
                </Box>
            </Box>
        </Box>
    )
}

export default PriceTab
